import { Genders } from "../constants/Genders";
import { supabase } from "../supabase/supabase";
import { GetTodaysDateString } from "../utils/GetDate";

interface IStats {
    id?: number;
    startTimeText?: string;
    startTime?: number;
    endTime?: number;
    maleCount?: number;
    femaleCount?: number;
    otherCount?: number;
    dropoffCount?: number;
    rulesClickCount?: number;
    endedEarly?: boolean;
    skippedCards?: string;
    playedPunishmentCards?: string;
    winnerCards?: string;
    punishmentCardSubmitTimeInSeconds?: string;
    keyMomentsViewCount?: number;
}

export class Stats {
    id: number;
    startTimeText: string;
    startTime: number;
    endTime: number;
    maleCount: number;
    femaleCount: number;
    otherCount: number;
    dropoffCount: number;
    rulesClickCount: number;
    endedEarly: boolean;
    skippedCards: string;
    playedPunishmentCards: string;
    winnerCards: string;
    punishmentCardSubmitTimeInSeconds: string;
    keyMomentsViewCount: number;

    constructor(id: number, startTimeText: string, startTime: number, endTime: number, maleCount: number, femaleCount: number, otherCount: number, dropoffCount: number,
        rulesClickCount: number, endedEarly: boolean, skippedCards: string, playedPunishmentCards: string,
        winnerCards: string, punishmentCardSubmitTimeInSeconds: string, keyMomentsViewCount: number) {
        this.id = id;
        this.startTimeText = startTimeText;
        this.startTime = startTime;
        this.endTime = endTime;
        this.maleCount = maleCount;
        this.femaleCount = femaleCount;
        this.otherCount = otherCount;
        this.dropoffCount = dropoffCount;
        this.rulesClickCount = rulesClickCount;
        this.endedEarly = endedEarly;
        this.skippedCards = skippedCards;
        this.playedPunishmentCards = playedPunishmentCards;
        this.winnerCards = winnerCards;
        this.punishmentCardSubmitTimeInSeconds = punishmentCardSubmitTimeInSeconds;
        this.keyMomentsViewCount = keyMomentsViewCount;
    }

    static fromJson(json: IStats) {
        return new Stats(
            json.id ? json.id as number : 0,
            json.startTimeText ? json.startTimeText as string : "",
            json.startTime ? json.startTime as number : 0,
            json.endTime !== undefined ? json.endTime as number : 1,
            json.maleCount ? json.maleCount as number : 0,
            json.femaleCount ? json.femaleCount as number : 0,
            json.otherCount ? json.otherCount as number : 0,
            json.dropoffCount ? json.dropoffCount as number : 0,
            json.rulesClickCount ? json.rulesClickCount as number : 0,
            json.endedEarly ? json.endedEarly as boolean : false,
            json.skippedCards ? json.skippedCards as string : "",
            json.playedPunishmentCards ? json.playedPunishmentCards as string : "",
            json.winnerCards ? json.winnerCards as string : "",
            json.punishmentCardSubmitTimeInSeconds ? json.punishmentCardSubmitTimeInSeconds as string : "",
            json.keyMomentsViewCount ? json.keyMomentsViewCount as number : 0,
        );
    }

    static async addNewStats(gender: Genders) {
        const todaysDateString = GetTodaysDateString();
        const unixTime = Date.now();
        const { data, error } = await supabase
            .from("Stats")
            .insert({
                startTimeText: todaysDateString,
                startTime: unixTime,
                endTime: 0,
                maleCount: gender === Genders.male ? 1 : 0,
                femaleCount: gender === Genders.female ? 1 : 0,
                otherCount: gender === Genders.other ? 1 : 0,
                dropoffCount: 0,
                rulesClickCount: 0,
                announcementsClickCount: 0,
                endedEarly: false,
                skippedCards: "",
                playedPunishmentCards: "",
                winnerCards: "",
                punishmentCardSubmitTimeInSeconds: "",
                keyMomentsViewCount: 0,
            })
            .select(`id`);
        return this.fromJson(data![0]);
    }

    static async updateGenderCounts(statsId: number, gender: Genders) {
        const { data, error } = await supabase
            .from("Stats")
            .select(`
                maleCount,
                femaleCount,
                otherCount
                `)
            .eq("id", statsId);
        
        if (data!.length > 0) {
            const stats = this.fromJson(data![0]);

            const { error } = await supabase
                .from("Stats")
                .update({
                    maleCount: gender === Genders.male ? stats.maleCount + 1 : stats.maleCount,
                    femaleCount: gender === Genders.female ? stats.femaleCount + 1 : stats.femaleCount,
                    otherCount: gender === Genders.other ? stats.otherCount + 1 : stats.otherCount,
                })
                .eq("id", statsId);
        }
    }

    static async updateRulesClickCount(statsId: number) {
        const { data, error } = await supabase
            .from("Stats")
            .select(`
                rulesClickCount
                `)
            .eq("id", statsId);
        
        if (data!.length > 0) {
            const stats = this.fromJson(data![0]);

            const { error } = await supabase
                .from("Stats")
                .update({
                    rulesClickCount: stats.rulesClickCount + 1,
                })
                .eq("id", statsId);
        }
    }

    static async updateDropoffCount(statsId: number) {
        const { data, error } = await supabase
            .from("Stats")
            .select(`
                dropoffCount
                `)
            .eq("id", statsId);
        
        if (data!.length > 0) {
            const stats = this.fromJson(data![0]);

            const { error } = await supabase
                .from("Stats")
                .update({
                    dropoffCount: stats.dropoffCount + 1,
                })
                .eq("id", statsId);
        }
    }

    static async updateEndEarlyStats(statsId: number) {
        const unixTime = Date.now();
        const { data, error } = await supabase
            .from("Stats")
            .select(`
                endTime,
                dropoffCount
                `)
            .eq("id", statsId);
        
        if (data!.length > 0) {
            const stats = this.fromJson(data![0]);
            if (stats.endTime === 0) {
                const { error } = await supabase
                .from("Stats")
                .update({
                    endTime: unixTime,
                    dropoffCount: stats.dropoffCount + 1,
                    endedEarly: true,
                })
                .eq("id", statsId);
            }
        }
    }

    static async updateEndTime(statsId: number) {
        const unixTime = Date.now();
        const { data, error } = await supabase
            .from("Stats")
            .select(`
                endTime
                `)
            .eq("id", statsId);
        
        if (data!.length > 0) {
            const stats = this.fromJson(data![0]);
            if (stats.endTime === 0) {
                const { error } = await supabase
                .from("Stats")
                .update({
                    endTime: unixTime,
                })
                .eq("id", statsId);
            }
        }
    }

    static async updateSkippedCards(statsId: number, redCardIdx: number) {
        const { data, error } = await supabase
            .from("Stats")
            .select(`
                skippedCards
                `)
            .eq("id", statsId);
        
        if (data!.length > 0) {
            const stats = this.fromJson(data![0]);
            const newSkippedCards = stats.skippedCards + String(redCardIdx) + ",";
            const { error } = await supabase
                .from("Stats")
                .update({
                    skippedCards: newSkippedCards,
                })
                .eq("id", statsId);
        }
    }

    static async updatePlayedPunishmentCards(statsId: number, blackCardIdx: number, submitTimeInSeconds: number) {
        const { data, error } = await supabase
            .from("Stats")
            .select(`
                playedPunishmentCards,
                punishmentCardSubmitTimeInSeconds
                `)
            .eq("id", statsId);
        
        if (data!.length > 0) {
            const stats = this.fromJson(data![0]);
            const newPlayedPunishmentCards = stats.playedPunishmentCards + String(blackCardIdx) + ",";
            const newSubmitTimeInSeconds = stats.punishmentCardSubmitTimeInSeconds + String(submitTimeInSeconds) + ",";
            const { error } = await supabase
                .from("Stats")
                .update({
                    playedPunishmentCards: newPlayedPunishmentCards,
                    punishmentCardSubmitTimeInSeconds: newSubmitTimeInSeconds,
                })
                .eq("id", statsId);
        }
    }

    static async updateWinnerCards(statsId: number, blackCardIdx: number) {
        const { data, error } = await supabase
            .from("Stats")
            .select(`
                winnerCards
                `)
            .eq("id", statsId);
        
        if (data!.length > 0) {
            const stats = this.fromJson(data![0]);
            const newWinnerCards = stats.winnerCards + String(blackCardIdx) + ",";
            const { error } = await supabase
                .from("Stats")
                .update({
                    winnerCards: newWinnerCards,
                })
                .eq("id", statsId);
        }
    }

    static async updateKeyMomentsViewCount(statsId: number) {
        const { data, error } = await supabase
            .from("Stats")
            .select(`
                keyMomentsViewCount
                `)
            .eq("id", statsId);
        
        if (data!.length > 0) {
            const stats = this.fromJson(data![0]);
            const { error } = await supabase
                .from("Stats")
                .update({
                    keyMomentsViewCount: stats.keyMomentsViewCount + 1,
                })
                .eq("id", statsId);
        }
    }
}